import React, { useEffect, useRef, useState } from 'react';
import { X, ArrowDown } from 'lucide-react';

/**
 * Full-text disclosure viewer. Agreeing needs the reader to reach the end of
 * the text (or the text fits without scrolling); until then the main button
 * scrolls the text down instead, so it never looks dead on a phone.
 *
 * `position` ({ index, total }) labels "2 of 5"; `hasNext` changes the agree
 * button to "Agree & next". With `closeOnAccept={false}` the parent decides
 * what happens after agreeing (e.g. open the next disclosure).
 */
const DisclosureModal = ({
  disclosure,
  open,
  onClose,
  onAccept,
  accepted = false,
  readOnly = false,
  position = null,
  hasNext = false,
  closeOnAccept = true
}) => {
  const scrollRef = useRef(null);
  const closeRef = useRef(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  // Parents pass an inline onClose; keep it in a ref so a parent re-render does
  // not re-run the open effect (which would reset "read to the end" and focus).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    setReachedEnd(false);
    const node = scrollRef.current;
    const check = () => {
      if (!node) return;
      if (node.scrollHeight - node.scrollTop - node.clientHeight < 24) setReachedEnd(true);
    };
    const timer = setTimeout(check, 50);
    node?.addEventListener('scroll', check);
    closeRef.current?.focus();
    const onKey = (event) => { if (event.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(timer);
      node?.removeEventListener('scroll', check);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, disclosure?.id]);

  if (!open || !disclosure) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-labelledby="disclosure-title">
      <div className="absolute inset-0 bg-gray-900/60" onClick={onClose} aria-hidden="true" />
      <div className="relative flex h-[92vh] w-full flex-col rounded-t-2xl bg-white shadow-xl sm:h-auto sm:max-h-[85vh] sm:max-w-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <h2 id="disclosure-title" className="text-lg font-semibold text-gray-900">{disclosure.title}</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {position ? `Disclosure ${position.index} of ${position.total} · ` : ''}Version {disclosure.version}{disclosure.standalone ? ' · standalone disclosure' : ''}
            </p>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="space-y-4 text-sm leading-6 text-gray-800">
            {disclosure.paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          </div>
        </div>
        <div className="border-t border-gray-200 px-5 py-4" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
          {!readOnly ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className={`text-xs ${reachedEnd ? 'text-green-700' : 'font-medium text-amber-700'}`} aria-live="polite">
                {reachedEnd ? 'You have read the full text.' : (<span className="inline-flex items-center gap-1"><ArrowDown className="h-3.5 w-3.5" aria-hidden="true" /> Read to the end to agree</span>)}
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:flex-none sm:py-2">Close</button>
                {reachedEnd ? (
                  <button
                    type="button"
                    onClick={() => { onAccept(disclosure.id); if (closeOnAccept) onClose(); }}
                    className="flex-[2] rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:flex-none sm:py-2"
                  >
                    {hasNext ? 'I agree, next disclosure' : (accepted ? 'Agreed' : 'I have read and agree')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => scrollRef.current?.scrollBy({ top: scrollRef.current.clientHeight * 0.85, behavior: 'smooth' })}
                    className="inline-flex flex-[2] items-center justify-center gap-1 rounded-md border border-blue-600 bg-white px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:flex-none sm:py-2"
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden="true" /> Keep reading
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DisclosureModal;
